import { LayoutGrid, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { useGetUser } from '@/hooks/useGetUser';

interface ExternalTool {
  id: string;
  nameKey: string;
  icon: string;
  href: string;
  active?: boolean;
}

const HUB_URL = import.meta.env.VITE_HUB_URL ?? '';
const CDK_URL = import.meta.env.VITE_CDK_URL ?? '#';
const CODE_URL = import.meta.env.VITE_CODE_URL ?? '#';
// Flow (formerly Build) is its own app, not a Hub route — the old
// `${HUB_URL}builder` path has no matching route in the Hub.
const FLOW_URL = import.meta.env.VITE_BUILDER_URL ?? 'https://flow.finalpos.com';

const externalTools: ExternalTool[] = [
  {
    id: 'build',
    nameKey: 'ToolSwitcher.build',
    icon: '/Build.svg',
    href: FLOW_URL,
  },
  {
    id: 'scale',
    nameKey: 'ToolSwitcher.scale',
    icon: '/Scale.svg',
    href: CDK_URL,
    active: true,
  },
  {
    id: 'code',
    nameKey: 'ToolSwitcher.code',
    icon: '/Code.svg',
    href: CODE_URL,
  },
];

const hubIcons = [
  { src: '/Manage.svg', altKey: 'ToolSwitcher.manage', z: 3, x: -18, rotate: -6 },
  { src: '/Run.svg', altKey: 'ToolSwitcher.run', z: 2, x: 0, rotate: 0 },
  { src: '/Pay.svg', altKey: 'ToolSwitcher.pay', z: 1, x: 18, rotate: 6 },
] as const;

function ToolCard({ tool }: { tool: ExternalTool }) {
  const { t } = useTranslation();

  return (
    <button
      type='button'
      onClick={() => {
        if (!tool.active) window.open(tool.href, '_blank', 'noopener,noreferrer');
      }}
      className={`group flex flex-col items-center gap-1.5 rounded-xl p-3 transition-all duration-200 hover:bg-accent hover:shadow-sm ${tool.active ? 'bg-accent' : ''}`}
    >
      <img
        src={tool.icon}
        alt={t(tool.nameKey)}
        width={40}
        height={40}
        className='rounded-[10px] transition-transform duration-200 group-hover:scale-110'
        draggable={false}
      />
      <p className='text-xs font-medium leading-none'>{t(tool.nameKey)}</p>
    </button>
  );
}

function getUserInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0) ?? '';
  const last = lastName?.charAt(0) ?? '';
  return (first + last).toUpperCase();
}

export function ToolSwitcher() {
  const { t } = useTranslation();
  const { currentUser } = useGetUser();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          aria-label={t('ToolSwitcher.switch_tools')}
        >
          <LayoutGrid className='h-4 w-4' />
        </Button>
      </PopoverTrigger>

      <PopoverContent align='end' sideOffset={8} className='w-[280px] p-0'>
        {/* Merchant Hub section */}
        <div className='mx-3 my-4 flex items-center justify-center gap-3 rounded-xl bg-accent px-3 py-4'>
          <div className='relative flex h-[40px] w-[76px] shrink-0 items-center justify-center'>
            {hubIcons.map((icon) => (
              <img
                key={icon.altKey}
                src={icon.src}
                alt={t(icon.altKey)}
                className='absolute h-10 w-10 rounded-[10px] shadow-sm'
                style={{
                  zIndex: icon.z,
                  transform: `translateX(${icon.x}px) rotate(${icon.rotate}deg)`,
                }}
                draggable={false}
              />
            ))}
          </div>
          <a href={`${HUB_URL}select-company`} className='flex flex-col gap-1 min-w-0'>
            <span className='text-sm font-semibold leading-none'>
              {t('ToolSwitcher.merchant_hub')}
            </span>
            <p className='text-xs text-muted-foreground leading-snug'>
              {t('ToolSwitcher.merchant_hub_description')}
            </p>
          </a>
        </div>

        <div className='h-px bg-border' />

        {/* External tools grid */}
        <p className='px-4 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>
          {t('ToolSwitcher.organization_tools')}
        </p>
        <div className='grid grid-cols-3 gap-1 px-2 pb-2'>
          {externalTools.map((tool) => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </div>

        <div className='h-px bg-border' />

        {/* Account section */}
        <div className='flex items-center gap-3 px-4 py-3'>
          <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground'>
            {getUserInitials(currentUser?.firstName, currentUser?.lastName)}
          </div>
          <div className='flex flex-col min-w-0'>
            <span className='text-sm font-medium truncate'>
              {currentUser?.firstName} {currentUser?.lastName}
            </span>
          </div>
          <a
            href={`${HUB_URL}logout`}
            className='ml-auto flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors'
            title={t('NavUser.sign_out')}
          >
            <LogOut className='h-4 w-4' />
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
